import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { crossTenantStyles as styles, crossTenantStatusStyle as statusStyle } from './crossTenantStyles';
import { CrossTenantGroupedList } from './CrossTenantGroupedList';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Platform operator: cross-tenant Giving / Fundraising view (2-pane, read-only; single-pane on mobile).
 *
 * Left list (campaign, org, status, raised), right detail pane (org, type,
 * dates, goal/raised progress, revenue-stream breakdown). Spans ALL tenants via
 * GET /platform/fundraising — operator space only. Deliberately read-only:
 * operators oversee campaigns but don't run a single org's fundraising.
 *
 * Styled via the shared crossTenantStyles stylesheet so it stays pixel-identical
 * to the Events page.
 */

interface FundraisingRow {
  id: string;
  name: string;
  type: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  goalAmount: number;
  raised: number;
  donorCount: number;
}

interface FundraisingDetail {
  id: string;
  name: string;
  type: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  tenant: { id: string; name: string; slug: string } | null;
  goalAmount: number;
  raised: number;
  progressPercent: number;
  donorCount: number;
  sponsorCount: number;
  contributionCount: number;
  memberLinkCount: number;
  pledgedTotal: number;
  sponsorTotal: number;
  donorTotal: number;
  contributionTotal: number;
  walkRunTotal: number;
  tshirtTotal: number;
  streams: string[];
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'Date TBD';
  return iso.split('T')[0];
}

function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function PlatformFundraisingPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<FundraisingRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FundraisingDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await apiClient<FundraisingRow[]>('/platform/fundraising');
        if (!cancelled) {
          setActivities(d);
          const target = routeId ? d.find((a) => a.id === routeId) : undefined;
          if (target) setSelectedId(target.id);
          else if (d.length > 0) setSelectedId(d[0].id);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load fundraising');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const d = await apiClient<FundraisingDetail>(`/platform/fundraising/${selectedId}`);
        if (!cancelled) setDetail(d);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const filtered = activities.filter((a) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      a.name.toLowerCase().includes(q) ||
      a.tenantName.toLowerCase().includes(q) ||
      a.type.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>Giving</h1>
          <p style={styles.sub}>Every fundraising campaign across all organizations on the platform.</p>
        </div>
        <input
          style={styles.search}
          placeholder="Search campaign, org, type…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {loading ? (
        <div style={styles.muted}>Loading fundraising…</div>
      ) : (
        <div style={isMobile ? styles.onePane : styles.twoPane}>
          {/* Left: org-grouped collapsible list (hidden on mobile when viewing detail) */}
          {(!isMobile || mobileView === 'list') && (
          <div style={styles.listPane}>
            <CrossTenantGroupedList
              rows={filtered}
              selectedId={selectedId}
              onSelect={(a) => { setSelectedId(a.id); setMobileView('detail'); navigate(`/platform/fundraising/${a.id}`, { replace: true }); }}
              renderTitle={(a) => a.name}
              renderMeta={(a) => `🎯 ${a.type} · 📅 ${fmtDate(a.startDate)}`}
              renderTrailing={(a) => money(a.raised)}
              statusOf={(a) => a.status}
              itemNoun="campaign"
              emptyLabel="No campaigns match."
              forceExpand={!!query.trim()}
            />
          </div>
          )}

          {/* Right: detail (hidden on mobile when viewing the list) */}
          {(!isMobile || mobileView === 'detail') && (
          <div style={styles.detailPane}>
            {isMobile && (
              <button type="button" style={styles.mobileBack} onClick={() => setMobileView('list')}>
                ← Back to list
              </button>
            )}
            {detailLoading ? (
              <div style={styles.muted}>Loading…</div>
            ) : !detail ? (
              <div style={styles.detailEmpty}>Select a campaign to see its details.</div>
            ) : (
              <>
                <div style={styles.detailHead}>
                  <div style={styles.detailTitleStack}>
                    {detail.tenant && (
                      <span style={styles.detailOrg}>🏢 {detail.tenant.name}</span>
                    )}
                    <h2 style={styles.detailName}>{detail.name}</h2>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <span style={styles.badgeFree}>💚 {detail.type}</span>
                    <span style={{ ...styles.pill, ...statusStyle(detail.status) }}>{detail.status}</span>
                  </div>
                </div>

                <div style={styles.ownerRow}>
                  🏢 <strong>{detail.tenant?.name ?? '(no organization)'}</strong>
                  {detail.tenant?.slug && <span style={styles.slug}>/{detail.tenant.slug}</span>}
                </div>
                <div style={styles.ownerRow}>
                  📅 {fmtDate(detail.startDate)} → {fmtDate(detail.endDate)}
                </div>

                {/* Goal progress */}
                <div style={styles.obWrap}>
                  <div style={styles.obHead}>
                    <span>Raised {money(detail.raised)} of {money(detail.goalAmount)} goal</span>
                    <span style={{ fontWeight: 700, color: detail.progressPercent >= 100 ? '#166534' : '#92400e' }}>
                      {detail.progressPercent}%
                    </span>
                  </div>
                  <div style={styles.obBar}>
                    <div style={{ ...styles.obFill, width: `${detail.progressPercent}%`, background: detail.progressPercent >= 100 ? '#16a34a' : '#f59e0b' }} />
                  </div>
                </div>

                <div style={styles.infoGrid}>
                  <Info label="Goal" value={money(detail.goalAmount)} />
                  <Info label="Raised" value={money(detail.raised)} />
                  <Info label="Donors" value={String(detail.donorCount)} />
                  <Info label="Sponsors" value={String(detail.sponsorCount)} />
                  <Info label="Sponsor received" value={money(detail.sponsorTotal)} />
                  <Info label="Member links" value={String(detail.memberLinkCount)} />
                </div>

                {detail.description && (
                  <div style={styles.block}>
                    <h3 style={styles.blockTitle}>Description</h3>
                    <div style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.5 }}>{detail.description}</div>
                  </div>
                )}

                <div style={styles.block}>
                  <h3 style={styles.blockTitle}>Revenue streams</h3>
                  {detail.streams.length === 0 ? (
                    <div style={styles.muted}>No contributions recorded yet.</div>
                  ) : (
                    <div style={styles.moduleWrap}>
                      {detail.streams.map((s) => (
                        <span key={s} style={styles.moduleChip}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.info}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value}</div>
    </div>
  );
}
